'use client';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert as MyAlert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { keys } from '@sola/contentful-model/keys.ts';
import type { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.ts';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import {
  type ComponentBaseProps,
  type FlexibleContentProps,
} from '@sola/schema/lib/schema.ts';

type AlertProps = FlexibleContentProps & ComponentBaseProps;

type AlertVariant = NonNullable<DisplayPurposeProps['variants']>;

const defaultAlertSettings = {
  variant: 'neutral',
  noIcon: true,
  dismissible: false,
} satisfies {
  variant: AlertVariant;
  noIcon: boolean;
  dismissible: boolean;
};

const getAction = (primaryCTA: AlertProps['primaryCTA']) => {
  if (!primaryCTA) {
    return;
  }

  const iconName = primaryCTA.icon
    ? IconNamesSafe.parse(primaryCTA.icon)
    : undefined;
  const target = primaryCTA.target || undefined;

  return (
    <Link
      download={primaryCTA.isDownload}
      href={primaryCTA.link}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      title={primaryCTA.title}
    >
      {iconName && <ArkIcon size="28" iconName={iconName} />}
      {primaryCTA.title}
    </Link>
  );
};

export const Alert: React.FC<AlertProps> = ({
  settings,
  title,
  body,
  primaryCTA,
}) => {
  const { variant, noIcon, dismissible } =
    settings?.type === keys.advancedComponents.alert
      ? settings
      : defaultAlertSettings;

  return (
    <MyAlert
      variant={variant}
      title={title}
      message={body}
      noIcon={noIcon}
      dismissible={dismissible}
      action={getAction(primaryCTA)}
    />
  );
};
